import { makeStyles } from '@material-ui/core';
import { CompetenceDto, UserDto } from 'utils/wemble-api.generated';

/** Shared shape of the competence tree for the person card's competencies
 *  section: both the Current list and the Grid slice the same tree. */

export const ALL_CATEGORIES = '__all__';
const OTHER = '__other__';

export type CategoryOption = { id: string; name: string; depth: number };
export type CompetenceSection = { label: string | null; competences: CompetenceDto[] };

export type CategoryNode = {
  id: string;
  name: string;
  category: CompetenceDto;
  /** Resolved parent in the tree; undefined for top-level (including
   *  categories whose stored parent was missing, self or looping). */
  parent?: CategoryNode;
  children: CategoryNode[];
  /** Competencies directly under this category. */
  leaves: CompetenceDto[];
};
type Node = CategoryNode;

const byName = (a: { name?: string | null }, b: { name?: string | null }) => (a.name || '').localeCompare(b.name || '');
const parentIdOf = (c: CompetenceDto): string | undefined => {
  const parent = c.parent as unknown;
  if (typeof parent === 'string') return parent;
  return (parent as { _id?: string | null } | null | undefined)?._id ?? undefined;
};

/** Categories (`root`) nest to any depth — Kvale runs three levels, e.g.
 *  Rettsområder › Bank og finans › Finans. A category whose parent chain is
 *  missing, points at itself or loops (real data has all three) is treated as
 *  top-level, so nothing becomes unreachable and nothing can loop. Leaves whose
 *  category can't be found go to "Other". */
export const buildTree = (competences: CompetenceDto[]) => {
  const categories = competences.filter((c) => c.root && c._id);
  const byId = new Map(categories.map((c) => [c._id as string, c]));

  // A category nests under its parent only if walking up from it reaches a
  // top-level category without revisiting anything.
  const attachedParent = (c: CompetenceDto): string | undefined => {
    const parentId = parentIdOf(c);
    if (!parentId || parentId === c._id || !byId.has(parentId)) return undefined;
    const seen = new Set<string>([c._id as string]);
    let current: string | undefined = parentId;
    while (current) {
      if (seen.has(current)) return undefined;
      seen.add(current);
      const node = byId.get(current);
      if (!node) return undefined;
      const next = parentIdOf(node);
      current = next && next !== current && byId.has(next) ? next : undefined;
    }
    return parentId;
  };

  const nodes = new Map<string, Node>(
    categories.map((c) => [c._id as string, { id: c._id as string, name: c.name || '', category: c, children: [], leaves: [] }]),
  );
  const tops: Node[] = [];
  categories.forEach((c) => {
    const node = nodes.get(c._id as string)!;
    const parentId = attachedParent(c);
    if (parentId) {
      node.parent = nodes.get(parentId)!;
      node.parent.children.push(node);
    } else tops.push(node);
  });
  const orphans: CompetenceDto[] = [];
  competences
    .filter((c) => !c.root)
    .forEach((c) => {
      const parentId = parentIdOf(c);
      const node = parentId ? nodes.get(parentId) : undefined;
      if (node) node.leaves.push(c);
      else orphans.push(c);
    });
  const sortNode = (node: Node) => {
    node.children.sort(byName);
    node.leaves.sort(byName);
    node.children.forEach(sortNode);
  };
  tops.sort(byName).forEach(sortNode);
  orphans.sort(byName);
  return { tops, nodes, orphans };
};

/** Every competence in the subtree, this category's own first. */
export const subtreeLeaves = (node: CategoryNode): CompetenceDto[] => [
  ...node.leaves,
  ...node.children.flatMap(subtreeLeaves),
];

/** Ids of this category and every category below it — the targets a move
 *  must never offer, since they would create a loop. */
export const subtreeCategoryIds = (node: CategoryNode): Set<string> => {
  const ids = new Set<string>();
  const walk = (n: CategoryNode) => {
    ids.add(n.id);
    n.children.forEach(walk);
  };
  walk(node);
  return ids;
};

/** Top-level category first, this category last. */
export const pathOf = (node: CategoryNode): CategoryNode[] => {
  const path: CategoryNode[] = [];
  for (let n: CategoryNode | undefined = node; n; n = n.parent) path.unshift(n);
  return path;
};

/** Every category and subcategory at any depth, indented by depth, so a firm
 *  with one broad category (Kvale's "Rettsområder") can narrow to a single area
 *  of law. Categories with nothing under them are left out. */
export const categoryOptions = (competences: CompetenceDto[]): CategoryOption[] => {
  const { tops, orphans } = buildTree(competences);
  const options: CategoryOption[] = [{ id: ALL_CATEGORIES, name: 'All categories', depth: 0 }];
  const hasContent = (node: Node): boolean => node.leaves.length > 0 || node.children.some(hasContent);
  const add = (node: Node, depth: number) => {
    if (!hasContent(node)) return;
    options.push({ id: node.id, name: node.name, depth });
    node.children.forEach((child) => add(child, depth + 1));
  };
  tops.forEach((node) => add(node, 0));
  if (orphans.length > 0) options.push({ id: OTHER, name: 'Other', depth: 0 });
  return options;
};

// Only competencies with a level set count towards the ordering, one each
// regardless of level: a competency added without a level says little about
// what the person actually does.
const heldIn = (competences: CompetenceDto[], held: Map<string, number> | null) =>
  held ? competences.filter((c) => c._id && (held.get(c._id) ?? -1) >= 0).length : 0;

/** One node as sections, in tree order: its own competencies, then each
 *  subcategory's, depth-first and A–Z. */
const sectionsOfNode = (node: Node, path: string[]): CompetenceSection[] => {
  const label = path.length > 0 ? path.join(' · ') : null;
  return [
    ...(node.leaves.length > 0 ? [{ label, competences: node.leaves }] : []),
    ...node.children.flatMap((child) => sectionsOfNode(child, [...path, child.name])),
  ];
};

/** Given the person, sections are ordered by how many competencies they hold
 *  in each, so everything they have comes first and empty groups sink to the
 *  bottom. This is per rendered section, not per branch: sorting whole branches
 *  let a branch with one held competency carry its empty subcategories above
 *  another branch's filled one. The sort is stable, so ties keep tree order. */
const byHeld = (sections: CompetenceSection[], held: Map<string, number> | null) => {
  if (!held) return sections;
  const counts = new Map(sections.map((section) => [section, heldIn(section.competences, held)]));
  return [...sections].sort((a, b) => counts.get(b)! - counts.get(a)!);
};

/** Sections to render for the selected category or subcategory. Labels are
 *  paths below what's selected — "All categories" labels from the top, so the
 *  flat list stays readable. Ordered most-held first, with "Other" always last. */
export const categorySections = (
  competences: CompetenceDto[],
  categoryId: string,
  user?: UserDto,
): CompetenceSection[] => {
  const { tops, nodes, orphans } = buildTree(competences);
  const held = user ? heldLevels(user) : null;
  const other = orphans.length > 0 ? [{ label: 'Other', competences: orphans }] : [];

  if (categoryId === ALL_CATEGORIES) {
    return [...byHeld(tops.flatMap((node) => sectionsOfNode(node, [node.name])), held), ...other];
  }
  if (categoryId === OTHER) return other.map((section) => ({ ...section, label: null }));
  const node = nodes.get(categoryId);
  return node ? byHeld(sectionsOfNode(node, []), held) : [];
};

/** Competence id -> level the person holds. Absent from the map means they do
 *  not hold it at all; -1 means held with no level set. */
export const heldLevels = (user: UserDto): Map<string, number> => {
  const map = new Map<string, number>();
  for (const skill of user.skills ?? []) {
    if (typeof skill === 'string') continue;
    const competenceId = typeof skill.competence === 'string' ? skill.competence : skill.competence?._id;
    if (!competenceId) continue;
    const level = skill.level;
    map.set(competenceId, typeof level === 'string' || !level ? -1 : level.level ?? -1);
  }
  return map;
};

const useStyles = makeStyles((theme) => ({
  // A quiet gray control rather than coloured pills: the category is a filter,
  // not the point of the section. Chevron and muted fill follow the small
  // selects used elsewhere in the app.
  select: {
    fontSize: 12,
    fontFamily: 'inherit',
    fontWeight: 500,
    color: theme.palette.gray.g2,
    backgroundColor: theme.palette.gray.g7,
    border: 'none',
    borderRadius: 6,
    height: 26,
    maxWidth: 220,
    textOverflow: 'ellipsis',
    padding: '0 24px 0 10px',
    cursor: 'pointer',
    outline: 'none',
    appearance: 'none' as const,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23999' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 9px center',
    transition: 'background-color 0.15s, color 0.15s',
    WebkitTapHighlightColor: 'transparent',
    '@media (hover: hover)': {
      '&:hover': {
        backgroundColor: theme.palette.gray.g9,
        color: theme.palette.text.primary,
      },
    },
  },
}));

export const CategorySelect = ({
  options,
  value,
  onChange,
}: {
  options: CategoryOption[];
  value: string;
  onChange: (id: string) => void;
}) => {
  const classes = useStyles();

  return (
    <select className={classes.select} value={value} onChange={(event) => onChange(event.target.value)}>
      {options.map((option) => (
        <option key={option.id} value={option.id}>
          {/* Native options can't be styled, so depth is shown with
              non-breaking spaces (one string: options take text only). */}
          {`${'\u00A0\u00A0\u00A0'.repeat(option.depth)}${option.name}`}
        </option>
      ))}
    </select>
  );
};
